import { IUserAuth } from 'entities';
import jwtDecode from 'jwt-decode';
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IIdentityAuthViewModel, IUserPlanViewModel } from 'viewModels';
import { useQueryClient } from 'react-query';
import { ModalAlertConfirmation } from '../components/Modals/ModalAlertConfirmation';
import { ILogin, UserController } from '../controller/UserController';
import { useGoogleEvents } from '../hooks/useGoogleEvents';
import usePersistedState from '../hooks/usePersistedState';
import { ProfileType, TutorialType, TypeUser } from '../types/enums';
import { CompanyController } from '../controller/CompanyController';
import { useTutorialProgress } from './tutorialProgress';
import useWebSocket from '../hooks/useWebSocket';

type UserAuthContextProps = {
  children: ReactNode;
};

interface IInitialValues {
  token?: string;
  provider?: string;
  user: IUserAuth;
  setUser: React.Dispatch<React.SetStateAction<IUserAuth>>;
  tokenIsValid: boolean;
  logout: () => Promise<void>;
  login: (form: ILogin) => Promise<ProfileType | undefined>;
  loginWithIdentity: (form: IIdentityAuthViewModel) => Promise<void>;
}

const UserAuthContext = React.createContext({} as IInitialValues);

const UserAuthContextProvider = ({ children }: UserAuthContextProps) => {
  const { signOut: signOutGoogle } = useGoogleEvents(new Date());
  const { setTutorialProgress, tutorialProgress } = useTutorialProgress();
  const queryClient = useQueryClient();
  const [token, setToken] = usePersistedState<string | undefined>('@legalhub:token', undefined);
  const [isIdentityAuthenticated, setIsIdentityAuthenticated] = useState(false);
  const [user, setUser] = usePersistedState<IUserAuth>('@legalhub:user', {} as IUserAuth);
  const [, setRefreshToken] = usePersistedState<string | undefined>(
    '@legalhub:refreshToken',
    undefined,
  );
  const [provider, setProvider] = usePersistedState<string | undefined>(
    '@legalhub:provider',
    undefined,
    true,
  );
  const tokenIsValid = useMemo(
    () => (token ? (jwtDecode(token) as any).exp > Date.now() / 1000 : false),
    [token],
  );
  const defaultIdentityMsg = {
    title: '',
    text: '',
  };

  const [identityMsg, setIdentityMsg] = useState<{
    title: string;
    text: string;
  }>(defaultIdentityMsg);

  const login = useCallback(
    async (form: ILogin) =>
      UserController.login(form).then(async (response) => {
        let typeProfile: ProfileType | undefined;
        setToken(response.token);
        setUser(response.user);
        const defaultTutorialProgress: TutorialType[] =
          tutorialProgress?.filter(
            (t) =>
              t === TutorialType.LOGIN ||
              t === TutorialType.PASSWORD_RECOVERY ||
              t === TutorialType.REGISTER ||
              t === TutorialType.REACTIVATE_ACCOUNT,
          ) ?? [];
        const newTutorialProgress = [
          ...defaultTutorialProgress,
          ...response.user?.tutorialProgress,
        ];
        setTutorialProgress(newTutorialProgress);
        if (response.user.type === TypeUser.COLLABORATOR) {
          CompanyController.getCollaboratorById(response.user.id).then((collaboratorInfo) => {
            setUser({ ...response.user, collaboratorInfo });
            typeProfile = collaboratorInfo.profileType;
          });
        }
        setRefreshToken(response.refreshToken);
        return typeProfile;
      }),
    [setToken, setUser, tutorialProgress, setTutorialProgress, setRefreshToken],
  );
  const loginWithIdentity = useCallback(
    (form: IIdentityAuthViewModel) => {
      const promise = new Promise<void>((resolve) => {
        if (form.success && form?.user) {
          setIsIdentityAuthenticated(true);
          setToken(form.token);
          setUser(form.user);
          setProvider(form.provider);
          setRefreshToken(form.refreshToken);
        } else {
          setIdentityMsg({
            text: form.error || 'Ocorreu um erro ao realizar o login',
            title: 'Conta cadastrada com sucesso',
          });
        }
        resolve();
      });
      return promise;
    },
    [setToken, setUser, setProvider, setRefreshToken],
  );
  const clearAuth = useCallback(() => {
    setToken(undefined);
    setUser({} as IUserAuth);
    setProvider(undefined);
    localStorage.removeItem('themeApp');
    localStorage.removeItem('fieldsRequireds');
  }, [setProvider, setToken, setUser]);

  const logout = useCallback(async () => {
    clearAuth();
    setRefreshToken(undefined);
    signOutGoogle();
    queryClient.clear();
    queryClient.resetQueries();
    const defaultTutorialProgress =
      tutorialProgress?.filter(
        (t) =>
          t === TutorialType.LOGIN ||
          t === TutorialType.PASSWORD_RECOVERY ||
          t === TutorialType.REGISTER ||
          t === TutorialType.REACTIVATE_ACCOUNT,
      ) || [];
    setTutorialProgress(defaultTutorialProgress);
  }, [
    clearAuth,
    setRefreshToken,
    signOutGoogle,
    queryClient,
    tutorialProgress,
    setTutorialProgress,
  ]);

  useWebSocket<IUserPlanViewModel>({
    url: `user/${user?.id}/user-plan`,
    onMessage: (userPlan) => {
      setUser((prev) => ({
        ...prev,
        userPlan: userPlan,
      }));
      window.location.href = "/feed"
    },
  });

  useEffect(() => {
    if (!tokenIsValid && isIdentityAuthenticated) {
      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenIsValid, isIdentityAuthenticated]);

  const handleCloseModal = () => setIdentityMsg(defaultIdentityMsg);
  const contextValue = useMemo(
    () => ({
      user,
      token,
      login,
      logout,
      setUser,
      provider,
      tokenIsValid,
      loginWithIdentity,
    }),
    [login, loginWithIdentity, logout, provider, setUser, token, tokenIsValid, user],
  );
  return (
    <UserAuthContext.Provider value={contextValue}>
      {children}
      <ModalAlertConfirmation
        isOpen={identityMsg.text !== ''}
        onConfirm={handleCloseModal}
        data={{
          ...identityMsg,
          type: 'generic',
          optionCancel: { active: false },
          optionConfirm: { text: 'Ok' },
        }}
        onRequestClose={handleCloseModal}
      />
    </UserAuthContext.Provider>
  );
};
export const useAuth = () => useContext(UserAuthContext);
export default UserAuthContextProvider;
